export const ADDITIONAL_KEY = '__additional';

export const humanize = (status) => {
  if (!status) return '';
  return String(status)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const sentence = (code) => {
  const words = String(code || '')
    .split('_')
    .filter(Boolean);
  if (!words.length) return '';
  const text = words.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const observedText = (finding) => String(finding?.observed || '').trim();

/** How a field should read in the GM table. */
export const fieldKind = (finding) => {
  const observed = observedText(finding);
  const status = finding?.status || '';
  if (observed === 'optional_blank') return 'optional';
  if (status === 'na' || status === 'not_applicable') return 'na';
  if (status === 'missing') return 'missing';
  if (status === 'pass' || status === 'accepted') return 'entered';
  if (!observed) return 'missing';
  return 'invalid';
};

export const enteredValue = (finding) => {
  const kind = fieldKind(finding);
  const observed = observedText(finding);
  if (kind === 'optional') return 'Blank · optional';
  if (kind === 'na') {
    if (observed && observed !== 'not_applicable' && observed !== 'na') return observed;
    return 'Marked not applicable';
  }
  if (kind === 'missing') return 'Not entered';
  return observed || 'Not entered';
};

const ENTRY_BADGE = {
  optional: 'Optional blank',
  na: 'Not applicable',
  missing: 'Missing',
  entered: 'Entered',
  invalid: 'Invalid entry',
};

export const entryBadge = (finding) => ENTRY_BADGE[fieldKind(finding)];

export const isPhotoFinding = (finding) =>
  Boolean(
    finding?.is_image ||
      finding?.is_signature ||
      finding?.has_field_preview ||
      (finding?.evidence_attachment_ids || []).length
  );

/** Plain-language reason the field was flagged. Empty when nothing is wrong. */
export const whyFlagged = (finding) => {
  const kind = fieldKind(finding);
  if (kind === 'entered' || kind === 'optional') return '';
  if (kind === 'na') return 'Excluded from this job’s score.';
  const observed = observedText(finding);
  if (isPhotoFinding(finding) && observed && observed.length > 24) return observed;
  const reason = sentence(finding?.reason_code);
  if (reason) {
    const expected = String(finding?.expected || '').trim();
    if (expected && expected !== observed) return `${reason}. Expected ${expected}.`;
    return `${reason}.`;
  }
  return sentence(finding?.status);
};

/** What the technician should do next. Empty when the audit did not ask for a change. */
export const nextStep = (finding) => {
  const action = String(finding?.technician_action || '').trim();
  if (action) return action;
  const kind = fieldKind(finding);
  if (kind === 'entered' || kind === 'optional') return 'No correction needed.';
  return '';
};

export const matchesFieldFilter = (finding, filter) => {
  if (!filter || filter === 'all') return true;
  const kind = fieldKind(finding);
  if (filter === 'attention') return kind === 'missing' || kind === 'invalid';
  if (filter === 'entered') return kind === 'entered';
  return true;
};

export const needsAttention = (finding) => matchesFieldFilter(finding, 'attention');

export const buildCategories = (items, findings) => {
  const linked = new Set();
  const categories = (items || []).map((item) => {
    const rows = (findings || []).filter((finding) =>
      (item.finding_ids || []).includes(finding.finding_id)
    );
    rows.forEach((finding) => linked.add(finding.finding_id));
    return {
      key: item.item_id,
      name: item.label || item.item_id,
      status: item.status,
      weight: item.weight,
      issueLabel: item.issue_label || '',
      findings: rows,
      additional: false,
    };
  });
  const extra = (findings || []).filter((finding) => !linked.has(finding.finding_id));
  if (extra.length || !categories.length) {
    categories.push({
      key: ADDITIONAL_KEY,
      name: categories.length ? 'Additional findings' : 'Findings',
      status: '',
      weight: null,
      issueLabel: '',
      findings: extra,
      additional: true,
    });
  }
  return categories.filter((category) => category.additional || category.key);
};

export const defaultCategoryKey = (categories) => {
  const list = categories || [];
  const failing = list.find((category) => !category.additional && category.status === 'fail');
  if (failing) return failing.key;
  const extraAttention = list.find(
    (category) => category.additional && category.findings.some(needsAttention)
  );
  if (extraAttention) return extraAttention.key;
  const withFindings = list.find((category) => !category.additional && category.findings.length);
  if (withFindings) return withFindings.key;
  const extra = list.find((category) => category.additional && category.findings.length);
  if (extra) return extra.key;
  return list[0]?.key || '';
};

export const categoryDot = (category) => {
  if (category.additional) return category.findings.some(needsAttention) ? 'attention' : 'pass';
  if (category.status === 'pass') return 'pass';
  if (category.status === 'na') return 'na';
  return 'attention';
};

export const pointsLabel = (category) => {
  if (category.additional) return 'Field findings';
  const weight = Number(category.weight) || 0;
  if (category.status === 'pass') return `${weight} / ${weight} points`;
  if (category.status === 'fail') return `0 / ${weight} points`;
  if (category.status === 'na') return `Excluded · ${weight} points`;
  return `Pending · ${weight} points`;
};

export const categoryStatusLabel = (category) => {
  if (category.additional) return 'Review findings';
  if (category.status === 'pass') return 'Passed';
  if (category.status === 'fail') return 'Correction needed';
  if (category.status === 'na') return 'Not applicable';
  if (category.status === 'pending') return 'Pending review';
  return humanize(category.status);
};

export const checklistCounts = (items) => ({
  passed: (items || []).filter((item) => item.status === 'pass').length,
  notApplicable: (items || []).filter((item) => item.status === 'na').length,
  needsCorrection: (items || []).filter((item) => item.status === 'fail').length,
});

export const correctionSummary = (issues) =>
  (issues || [])
    .map((issue) => String(issue || '').trim())
    .filter(Boolean)
    .map((issue) => (issue.endsWith('.') ? issue : `${issue}.`))
    .join(' ');
